import { defineComponent as _defineComponent } from 'vue'
import Modal from "../Modal.vue";
  import { reactive, ref } from "vue";
  import CheckboxInput from "../input/CheckboxInput.vue";
  import useVuelidate from "@vuelidate/core";
  import { required, email, helpers } from "@vuelidate/validators";
  import Cleave from "vue-cleave-component";
  import { phoneNumberOption, phonePlaceholder } from "@/helpers/cleavePlugin";
  import { validPhoneNumber } from "@/helpers/validation";
  import { resetVuelidateErrors } from "@/helpers/vuelidate";
  import store from "@/store";
  import LeadSourceEnum from "@/enums/LeadSourceEnum";
  import { useToast, POSITION } from "vue-toastification";
  import { TimeToBuildEnum } from "@/enums/BuilderTimeToBuild";
  import { BestDescribesYouEnum } from "@/enums/BuilderBestDescribesYou";
  import { FinanceStatusEnum } from "@/enums/BuilderFinanceStatus";
  import DropDown from "@/components/ui/dropdown/DropDown.vue";
  import { eventTracking } from "@/helpers/googleAnalyticsEventTracking";
  import {
    EventActionName,
    EventByCategory,
    EventLabelByName,
    EventByAction,
  } from "@/types/EventTracking";
  import { CustomizeHouseStepsEnum } from "@/enums/CustomizeHouseSteps";
  import KlaviyoErrorEnum from "@/enums/KlaviyoEnum";
  import axios, { AxiosError } from "axios";
  import ErrorMessages from "@/constants/errorsMessages";
  import parseAustralianNumberToInternational from "@/helpers/phone-number";
  import { handleError } from "@/helpers/klaviyo/klaviyo-error-handler.helper";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'BuilderRequestQuoteModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    house: {
      type: Object,
      required: true,
    },
    lot: {
      type: Object,
      required: true,
    },
    estate: {
      type: Object,
      required: true,
    },
    isPhoneVerificationModal: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    "close",
    "back",
    "send",
    "openPhoneVerificationModal",
  ],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

  const toast = useToast();

  const emits = __emit;

  const props = __props;

  const rules = {
    firstName: { required },
    lastName: { required },
    email: { required, email },
    phone: {
      required,
      validPhoneNumber: helpers.withMessage(
        "Value must be valid phone number",
        validPhoneNumber
      ),
    },
    timeToBuild: { required },
    bestDescribesYou: { required },
    financeStatus: { required },
    additionalRequest: { optional: true },
  };

  const formFields = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    timeToBuild: TimeToBuildEnum.THREE_MONTHS,
    bestDescribesYou: BestDescribesYouEnum.FIRST_TIME_BUYER,
    financeStatus: FinanceStatusEnum.PRE_APPROVED,
    additionalRequest: "",
    emailSentToLandowner: false,
    isAdminPackage: false,
  };
  const form = reactive(Object.assign({}, formFields));
  const v$ = useVuelidate(rules, form);
  const isLoading = ref(false);

  const onClose = () => {
    resetVuelidateErrors(v$);
    emits("close");
  };

  const selectBuildPlan = (timeToBuild: TimeToBuildEnum) => {
    form.timeToBuild = timeToBuild;
  };
  const selectDescribesYou = (bestDescribesYou: BestDescribesYouEnum) => {
    form.bestDescribesYou = bestDescribesYou;
  };
  const selectFinanceStatus = (financeStatus: FinanceStatusEnum) => {
    form.financeStatus = financeStatus;
  };
  const updateAllQuotes = () => {
    store.commit("builder/updateCurrentStepForAllQuotes");
  };
  const checkQuotesAreValid = () => {
    updateAllQuotes();
    const { userSelectionDesign } = store.state.builder;
    const areAllStepsExterior = userSelectionDesign.every(
      (el) => el.currentStep === CustomizeHouseStepsEnum.CONFIRMATION
    );

    if (!areAllStepsExterior) {
      toast.error(
        "Error sending builder quote, please ensure all steps are completed"
      );
      return false;
    }

    return true;
  };
  const verifyProfile = async (email: string, phone: string) => {
    const normalizedPhone = parseAustralianNumberToInternational(phone);

    return store.dispatch("klaviyo/verifyProfile", {
      email,
      phone_number: normalizedPhone,
    });
  };

  const createProfileAndSendSms = async (email: string, phone: string) => {
    const normalizedPhone = parseAustralianNumberToInternational(phone);

    await store.dispatch("klaviyo/createProfileAndSendVerificationSms", {
      email,
      phone_number: normalizedPhone,
    });
    const quote = createBodyRequest();

    emits("openPhoneVerificationModal", quote);
  };

  const submit = async () => {
    const isValid = await validateForm();

    if (!isValid) {
      return;
    }

    if (!checkQuotesAreValid()) {
      return;
    }

    try {
      isLoading.value = true;
      const body = createBodyRequest();
      const verifyResponse = await verifyProfile(form.email, form.phone);

      if (verifyResponse.isValid) {
        await store.dispatch("builder/sendQuote", body);
        emits("send");
      } else {
        await createProfileAndSendSms(form.email, form.phone);
      }

      convertToLead(body.lead.estateId);
      onClose();
    } catch (error) {
      handleError(error as AxiosError);
    } finally {
      isLoading.value = false;
    }
  };

  const validateForm = async () => {
    const isValid = await v$.value.$validate();

    v$.value.$touch();
    return isValid;
  };

  const createBodyRequest = () => {
    const { lot } = props;
    const { landownerId, lotId } = lot;
    const { additionalRequest } = form;
    const { userSelectionDesign } = store.state.builder;

    const userSelections = userSelectionDesign.map(
      ({ builder, house, facade, floorplan, lot }) => ({
        builder: {
          builderId: builder.id,
          name: builder.name,
          logoUrl: builder.logo,
        },
        house: {
          houseId: house.id,
          name: house.name,
          price: house.price,
        },
        facade: {
          facadeId: facade.id,
          name: facade.name,
          url: facade.url,
          price: facade.price,
        },
        floorplan: {
          name: floorplan?.name,
          url: floorplan.url,
        },
        lot: {
          lotId: lot.lotId,
          name: lot.name,
          price: lot.price,
        },
      })
    );

    return {
      selections: userSelections,
      additionalRequest,
      timeReference: form.timeToBuild,
      bestDescribesYou: form.bestDescribesYou,
      financeStatus: form.financeStatus,
      estateTitle: props.estate.title,
      estateId: props.estate._id,
      landownerId: landownerId?._id ?? lotId.landownerId,
      emailSentToLandowner: form.emailSentToLandowner,
      isAdminPackage: form.isAdminPackage,
      lead: {
        landownerId: landownerId?._id ?? lotId.landownerId,
        estateId: lotId?.estateId ?? lot.estateId,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email.toLowerCase(),
        phone: form.phone,
        source: LeadSourceEnum.ENQUIRY,
        watched: false,
      },
    };
  };
  const convertToLead = (estateById: string) => {
    eventTracking({
      actionsName: EventActionName.CONVERT_TO_LEAD,
      eventByCategory: EventByCategory.LEAD_CONVERSION,
      eventLabelByName: EventLabelByName.REQUEST_BUILDER_HOLD,
      eventByAction: EventByAction.SUBMIT,
      estateById,
    });
  };

  const optionsDateForBuild = [
    TimeToBuildEnum.ASAP,
    TimeToBuildEnum.THREE_MONTHS,
    TimeToBuildEnum.SIX_MONTHS,
    TimeToBuildEnum.MORE_THAN_SIX_MONTHS,
  ];
  const optionsBestDescribes = [
    BestDescribesYouEnum.FIRST_TIME_BUYER,
    BestDescribesYouEnum.UPSIZER_OR_DOWNSIZER,
    BestDescribesYouEnum.INVESTOR,
  ];
  const optionsFinanceStatus = [
    FinanceStatusEnum.PRE_APPROVED,
    FinanceStatusEnum.APPLICATION_IN_PROGRESS,
    FinanceStatusEnum.EXPLORING_MY_OPTIONS,
    FinanceStatusEnum.CASH_BUYER,
  ];

const __returned__ = { toast, emits, props, rules, formFields, form, v$, isLoading, onClose, selectBuildPlan, selectDescribesYou, selectFinanceStatus, updateAllQuotes, checkQuotesAreValid, verifyProfile, createProfileAndSendSms, submit, validateForm, createBodyRequest, convertToLead, optionsDateForBuild, optionsBestDescribes, optionsFinanceStatus, Modal, reactive, ref, CheckboxInput, get useVuelidate() { return useVuelidate }, get required() { return required }, get email() { return email }, get helpers() { return helpers }, get Cleave() { return Cleave }, get phoneNumberOption() { return phoneNumberOption }, get phonePlaceholder() { return phonePlaceholder }, get validPhoneNumber() { return validPhoneNumber }, get resetVuelidateErrors() { return resetVuelidateErrors }, get store() { return store }, get LeadSourceEnum() { return LeadSourceEnum }, get useToast() { return useToast }, get POSITION() { return POSITION }, get TimeToBuildEnum() { return TimeToBuildEnum }, get BestDescribesYouEnum() { return BestDescribesYouEnum }, get FinanceStatusEnum() { return FinanceStatusEnum }, DropDown, get eventTracking() { return eventTracking }, get EventActionName() { return EventActionName }, get EventByCategory() { return EventByCategory }, get EventLabelByName() { return EventLabelByName }, get EventByAction() { return EventByAction }, get CustomizeHouseStepsEnum() { return CustomizeHouseStepsEnum }, get KlaviyoErrorEnum() { return KlaviyoErrorEnum }, get axios() { return axios }, get AxiosError() { return AxiosError }, get ErrorMessages() { return ErrorMessages }, get parseAustralianNumberToInternational() { return parseAustralianNumberToInternational }, get handleError() { return handleError } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})